import React, { useState } from 'react'
import styled from 'styled-components'
import classNames from 'classnames'
import dynamic from 'next/dynamic'
import { Grid, Col, Row } from 'react-styled-flexboxgrid'

import Image from 'components/Atoms/Image'
import Text from 'components/Atoms/Text'
import { useTranslation } from 'i18n-web/i18next'

const Slider = dynamic(() => import('components/Atoms/Slider'), { ssr: false })

const Component = styled.div`
  padding: 30px 0 80px;
  background: ${props => props.theme.palette.grayLighter};

  .pagination {
    bottom: -45px !important;
  }

  @media (min-width: ${props => props.theme.flexboxgrid.breakpoints.sm}rem) {
    padding: 80px 0;
  }
`

const Title = styled(Text)`
  margin-bottom: 30px;
  font-size: ${props => props.theme.font.size.mobile.h3};
  color: ${props => props.theme.palette.grayDark};
  text-align: center;
  text-transform: uppercase;

  @media (min-width: ${props => props.theme.flexboxgrid.breakpoints.sm}rem) {
    margin-bottom: 40px;
    font-size: ${props => props.theme.font.size.desktop.h3};
    text-align: left;
  }
`

const ListItem = styled.li`
  display: flex;
  align-items: center;

  @media (min-width: ${props => props.theme.flexboxgrid.breakpoints.sm}rem) {
    cursor: pointer;

    & + li {
      margin-top: 24px;
    }
  }
`

const Icon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  min-width: 60px;
  height: 60px;
  margin-right: 20px;
  background-color: ${props => props.theme.palette.grayDark};
  border-radius: 50%;
  box-shadow: 0 1px 4px 0 rgba(0, 0, 0, 0.35);
  transition: background 500ms;

  &.active {
    background-color: ${props => props.theme.palette.white};
  }

  img {
    width: auto;
    height: 35px;

    &.small {
      height: 25px;
    }

    &.medium {
      height: 30px;
    }
  }
`

const ListItemTitle = styled(Text)`
  font-size: ${props => props.theme.font.size.mobile.small};
  font-weight: 700;
  color: ${props => props.theme.palette.grayDark};

  @media (min-width: ${props => props.theme.flexboxgrid.breakpoints.sm}rem) {
    font-size: ${props => props.theme.font.size.desktop.regular};
  }
`

const ListItemDescription = styled(Text)`
  font-size: ${props => props.theme.font.size.mobile.small};
  color: ${props => props.theme.palette.grayDark};

  @media (min-width: ${props => props.theme.flexboxgrid.breakpoints.sm}rem) {
    font-size: ${props => props.theme.font.size.desktop.regular};
  }
`

const PhoneFrame = styled.div`
  position: relative;
  margin-bottom: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;

  @media (min-width: ${props => props.theme.flexboxgrid.breakpoints.md}rem) {
    margin-bottom: 0;
    height: 100%;
  }
`

const PhoneImage = styled(Image)`
  width: 240px;
  z-index: 10;

  @media (min-width: ${props => props.theme.flexboxgrid.breakpoints.md}rem) {
    width: 270px;
  }
`

const ActiveImage = styled(Image)`
  position: absolute;
  margin-top: -1px;
  margin-left: -2px;
  width: 212px;
  border-radius: 10px;
  z-index: 5;
  transition: opacity 500ms;
  opacity: 0;

  &.active {
    opacity: 1;
  }

  @media (min-width: ${props => props.theme.flexboxgrid.breakpoints.md}rem) {
    width: 239px;
  }
`

const Benefits = () => {
  const [active, setActive] = useState(1)

  const { t } = useTranslation()

  return (
    <Component>
      <Grid>
        <Row>
          <Col xs={12}>
            <Title as="h2" className="bold uppercase grayDark">
              {t('common.benefits.title')}
            </Title>
          </Col>
          <Col xs={12} sm={12} md={false}>
            <Slider
              options={{
                arrows: false,
                classes: {
                  pagination: 'splide__pagination pagination',
                },
                gap: '32px',
                perMove: 1,
                perPage: 1,
                width: '100vw',
              }}
              items={[
                <>
                  <PhoneFrame>
                    <ActiveImage
                      className="active"
                      src="/static/images/how-it-works/benefits/1.jpg"
                    />
                    <PhoneImage src="/static/images/how-it-works/benefits/iphone.png" />
                  </PhoneFrame>
                  <ListItem>
                    <Icon>
                      <Image src="/static/icons/icon-credits.svg" />
                    </Icon>
                    <div>
                      <ListItemTitle>
                        {t('common.benefits.benefit1.title')}
                      </ListItemTitle>
                      <ListItemDescription>
                        {t('common.benefits.benefit1.description')}
                      </ListItemDescription>
                    </div>
                  </ListItem>
                </>,
                <>
                  <PhoneFrame>
                    <ActiveImage
                      className="active"
                      src="/static/images/how-it-works/benefits/2.jpg"
                    />
                    <PhoneImage src="/static/images/how-it-works/benefits/iphone.png" />
                  </PhoneFrame>
                  <ListItem>
                    <Icon>
                      <Image
                        className="medium"
                        src="/static/icons/icon-more-spaces.svg"
                      />
                    </Icon>
                    <div>
                      <ListItemTitle>
                        {t('common.benefits.benefit2.title')}
                      </ListItemTitle>
                      <ListItemDescription>
                        {t('common.benefits.benefit2.description')}
                      </ListItemDescription>
                    </div>
                  </ListItem>
                </>,
                <>
                  <PhoneFrame>
                    <ActiveImage
                      className="active"
                      src="/static/images/how-it-works/benefits/3.jpg"
                    />
                    <PhoneImage src="/static/images/how-it-works/benefits/iphone.png" />
                  </PhoneFrame>
                  <ListItem>
                    <Icon>
                      <Image
                        className="medium"
                        src="/static/icons/icon-directions.svg"
                      />
                    </Icon>
                    <div>
                      <ListItemTitle>
                        {t('common.benefits.benefit3.title')}
                      </ListItemTitle>
                      <ListItemDescription>
                        {t('common.benefits.benefit3.description')}
                      </ListItemDescription>
                    </div>
                  </ListItem>
                </>,
                <>
                  <PhoneFrame>
                    <ActiveImage
                      className="active"
                      src="/static/images/how-it-works/benefits/4.jpg"
                    />
                    <PhoneImage src="/static/images/how-it-works/benefits/iphone.png" />
                  </PhoneFrame>
                  <ListItem>
                    <Icon>
                      <Image
                        className="medium"
                        src="/static/icons/icon-events.svg"
                      />
                    </Icon>
                    <div>
                      <ListItemTitle>
                        {t('common.benefits.benefit4.title')}
                      </ListItemTitle>
                      <ListItemDescription>
                        {t('common.benefits.benefit4.description')}
                      </ListItemDescription>
                    </div>
                  </ListItem>
                </>,
                <>
                  <PhoneFrame>
                    <ActiveImage
                      className="active"
                      src="/static/images/how-it-works/benefits/5.jpg"
                    />
                    <PhoneImage src="/static/images/how-it-works/benefits/iphone.png" />
                  </PhoneFrame>
                  <ListItem>
                    <Icon>
                      <Image
                        className="medium"
                        src="/static/icons/icon-validate.svg"
                      />
                    </Icon>
                    <div>
                      <ListItemTitle>
                        {t('common.benefits.benefit5.title')}
                      </ListItemTitle>
                      <ListItemDescription>
                        {t('common.benefits.benefit5.description')}
                      </ListItemDescription>
                    </div>
                  </ListItem>
                </>,
                <>
                  <PhoneFrame>
                    <ActiveImage
                      className="active"
                      src="/static/images/how-it-works/benefits/6.jpg"
                    />
                    <PhoneImage src="/static/images/how-it-works/benefits/iphone.png" />
                  </PhoneFrame>
                  <ListItem>
                    <Icon>
                      <Image
                        className="medium"
                        src="/static/icons/icon-customer-support.svg"
                      />
                    </Icon>
                    <div>
                      <ListItemTitle>
                        {t('common.benefits.benefit6.title')}
                      </ListItemTitle>
                      <ListItemDescription>
                        {t('common.benefits.benefit6.description')}
                      </ListItemDescription>
                    </div>
                  </ListItem>
                </>,
                <>
                  <PhoneFrame>
                    <ActiveImage
                      className="active"
                      src="/static/images/how-it-works/benefits/7.jpg"
                    />
                    <PhoneImage src="/static/images/how-it-works/benefits/iphone.png" />
                  </PhoneFrame>
                  <ListItem>
                    <Icon>
                      <Image
                        className="medium"
                        src="/static/icons/icon-credits.svg"
                      />
                    </Icon>
                    <div>
                      <ListItemTitle>
                        {t('common.benefits.benefit7.title')}
                      </ListItemTitle>
                      <ListItemDescription>
                        {t('common.benefits.benefit7.description')}
                      </ListItemDescription>
                    </div>
                  </ListItem>
                </>,
              ]}
            />
          </Col>
          <Col xs={false} sm={false} md={8}>
            <ul>
              <ListItem onClick={() => setActive(1)}>
                <Icon
                  className={classNames({
                    active: active === 1,
                  })}>
                  <Image src="/static/icons/icon-credits.svg" />
                </Icon>
                <div>
                  <ListItemTitle>
                    {t('common.benefits.benefit1.title')}
                  </ListItemTitle>
                  <ListItemDescription>
                    {t('common.benefits.benefit1.description')}
                  </ListItemDescription>
                </div>
              </ListItem>
              <ListItem onClick={() => setActive(2)}>
                <Icon
                  className={classNames({
                    active: active === 2,
                  })}>
                  <Image
                    className="medium"
                    src="/static/icons/icon-more-spaces.svg"
                  />
                </Icon>
                <div>
                  <ListItemTitle>
                    {t('common.benefits.benefit2.title')}
                  </ListItemTitle>
                  <ListItemDescription>
                    {t('common.benefits.benefit2.description')}
                  </ListItemDescription>
                </div>
              </ListItem>
              <ListItem onClick={() => setActive(3)}>
                <Icon
                  className={classNames({
                    active: active === 3,
                  })}>
                  <Image
                    className="medium"
                    src="/static/icons/icon-directions.svg"
                  />
                </Icon>
                <div>
                  <ListItemTitle>
                    {t('common.benefits.benefit3.title')}
                  </ListItemTitle>
                  <ListItemDescription>
                    {t('common.benefits.benefit3.description')}
                  </ListItemDescription>
                </div>
              </ListItem>
              <ListItem onClick={() => setActive(4)}>
                <Icon
                  className={classNames({
                    active: active === 4,
                  })}>
                  <Image
                    className="small"
                    src="/static/icons/icon-events.svg"
                  />
                </Icon>
                <div>
                  <ListItemTitle>
                    {t('common.benefits.benefit4.title')}
                  </ListItemTitle>
                  <ListItemDescription>
                    {t('common.benefits.benefit4.description')}
                  </ListItemDescription>
                </div>
              </ListItem>
              <ListItem onClick={() => setActive(5)}>
                <Icon
                  className={classNames({
                    active: active === 5,
                  })}>
                  <Image
                    className="medium"
                    src="/static/icons/icon-validate.svg"
                  />
                </Icon>
                <div>
                  <ListItemTitle>
                    {t('common.benefits.benefit5.title')}
                  </ListItemTitle>
                  <ListItemDescription>
                    {t('common.benefits.benefit5.description')}
                  </ListItemDescription>
                </div>
              </ListItem>
              <ListItem onClick={() => setActive(6)}>
                <Icon
                  className={classNames({
                    active: active === 6,
                  })}>
                  <Image
                    className="small"
                    src="/static/icons/icon-customer-support.svg"
                  />
                </Icon>
                <div>
                  <ListItemTitle>
                    {t('common.benefits.benefit6.title')}
                  </ListItemTitle>
                  <ListItemDescription>
                    {t('common.benefits.benefit6.description')}
                  </ListItemDescription>
                </div>
              </ListItem>
              <ListItem onClick={() => setActive(7)}>
                <Icon
                  className={classNames({
                    active: active === 7,
                  })}>
                  <Image src="/static/icons/icon-credits.svg" />
                </Icon>
                <div>
                  <ListItemTitle>
                    {t('common.benefits.benefit7.title')}
                  </ListItemTitle>
                  <ListItemDescription>
                    {t('common.benefits.benefit7.description')}
                  </ListItemDescription>
                </div>
              </ListItem>
            </ul>
          </Col>
          <Col xs={false} sm={false} md={4}>
            <PhoneFrame>
              <ActiveImage
                className={classNames({
                  active: active === 1,
                })}
                src="/static/images/how-it-works/benefits/1.jpg"
              />
              <ActiveImage
                className={classNames({
                  active: active === 2,
                })}
                src="/static/images/how-it-works/benefits/2.jpg"
              />
              <ActiveImage
                className={classNames({
                  active: active === 3,
                })}
                src="/static/images/how-it-works/benefits/3.jpg"
              />
              <ActiveImage
                className={classNames({
                  active: active === 4,
                })}
                src="/static/images/how-it-works/benefits/4.jpg"
              />
              <ActiveImage
                className={classNames({
                  active: active === 5,
                })}
                src="/static/images/how-it-works/benefits/5.jpg"
              />
              <ActiveImage
                className={classNames({
                  active: active === 6,
                })}
                src="/static/images/how-it-works/benefits/6.jpg"
              />
              <ActiveImage
                className={classNames({
                  active: active === 7,
                })}
                src="/static/images/how-it-works/benefits/7.jpg"
              />
              <PhoneImage src="/static/images/how-it-works/benefits/iphone.png" />
            </PhoneFrame>
          </Col>
        </Row>
      </Grid>
    </Component>
  )
}

export default Benefits
